/** Cross-panel refresh for Redis-backed ยอดค้าง / สลิปค้าง after match/undo. */

export const REFRESH_RECEIVABLE_PENDING_EVENT = "aera:refresh-receivable-pending";

export type RefreshReceivablePendingDetail = {
  /** Prefer silent UI (no full-table loading flash). */
  silent?: boolean;
  /** Bypass in-flight dedupe (default true for money ops). */
  force?: boolean;
  /** Extra silent refetches after Redis sync may still be catching up. */
  retries?: number;
  retryDelayMs?: number;
  reason?: string;
};

export function requestReceivablePendingRefresh(
  detail?: RefreshReceivablePendingDetail,
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(REFRESH_RECEIVABLE_PENDING_EVENT, {
      detail: {
        silent: true,
        force: true,
        retries: 2,
        retryDelayMs: 200,
        ...detail,
      } satisfies RefreshReceivablePendingDetail,
    }),
  );
}

export function subscribeReceivablePendingRefresh(
  listener: (detail: RefreshReceivablePendingDetail) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (event: Event) => {
    const detail =
      (event as CustomEvent<RefreshReceivablePendingDetail>).detail ?? {};
    listener(detail);
  };
  window.addEventListener(REFRESH_RECEIVABLE_PENDING_EVENT, handler);
  return () => {
    window.removeEventListener(REFRESH_RECEIVABLE_PENDING_EVENT, handler);
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

type ReceivableRefetch = (options?: {
  silent?: boolean;
  force?: boolean;
}) => void | Promise<void>;

/**
 * Force-refetch now (await), then silent retries in the background.
 * Callers that need the list fresh before closing a modal should await this —
 * they get the first forced read without waiting for every retry.
 */
export async function refetchReceivablePendingWithRetry(
  refetch: ReceivableRefetch,
  detail?: RefreshReceivablePendingDetail,
): Promise<void> {
  const silent = detail?.silent ?? true;
  const force = detail?.force ?? true;
  const retries = Math.max(0, detail?.retries ?? 2);
  const delayMs = Math.max(50, detail?.retryDelayMs ?? 200);

  await Promise.resolve(refetch({ silent, force }));

  if (retries <= 0) return;

  void (async () => {
    for (let i = 0; i < retries; i += 1) {
      await sleep(delayMs * (i + 1));
      await Promise.resolve(refetch({ silent: true, force: true }));
    }
  })();
}
